/**
 * Where a floating panel goes.
 *
 * Pure geometry, deliberately separate from the DOM: flipping and clamping are
 * the two things that break silently (a menu on the last row of a list opens
 * below the fold and is unreachable), so they are the two things that get tests.
 *
 * Every number is in viewport coordinates, which is what `getBoundingClientRect`
 * returns and what `position: fixed` consumes — no scroll offsets anywhere.
 */

export type FloatSide = "top" | "bottom" | "left" | "right";

export interface FloatRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface FloatSize {
  width: number;
  height: number;
}

export interface FloatInput {
  /** The trigger, in viewport coordinates. */
  anchor: FloatRect;
  /** The panel's natural size, unclamped. */
  panel: FloatSize;
  viewport: FloatSize;
  /** `vertical` flips below/above (menus, dropdowns); `horizontal` flips right/left (submenus). */
  axis: "vertical" | "horizontal";
  /** On the cross axis: `start` lines up leading edges, `end` lines up trailing edges. */
  align: "start" | "end";
  /** Distance between anchor and panel. */
  gap: number;
  /** Smallest distance the panel keeps from a viewport edge. */
  margin: number;
}

export interface FloatPlacement {
  left: number;
  top: number;
  side: FloatSide;
  maxHeight: number;
  maxWidth: number;
}

function clamp(value: number, min: number, max: number): number {
  // `max < min` on a viewport narrower than the panel: the low edge wins, so a
  // too-wide panel starts at the margin and scrolls rather than hanging off-left.
  return Math.max(min, Math.min(value, Math.max(min, max)));
}

export function placeFloating({
  anchor,
  panel,
  viewport,
  axis,
  align,
  gap,
  margin,
}: FloatInput): FloatPlacement {
  const maxWidth = Math.max(0, viewport.width - margin * 2);
  const width = Math.min(panel.width, maxWidth);

  if (axis === "vertical") {
    const anchorBottom = anchor.top + anchor.height;
    const below = viewport.height - anchorBottom - gap - margin;
    const above = anchor.top - gap - margin;
    // Prefer downwards; flip up only when the panel does not fit below AND
    // there is genuinely more room above — otherwise a flip just moves the clip.
    const side: FloatSide = panel.height <= below || below >= above ? "bottom" : "top";
    const room = Math.max(0, side === "bottom" ? below : above);
    const height = Math.min(panel.height, room);
    const rawTop = side === "bottom" ? anchorBottom + gap : anchor.top - gap - height;
    const rawLeft = align === "end" ? anchor.left + anchor.width - width : anchor.left;
    return {
      left: clamp(rawLeft, margin, viewport.width - margin - width),
      top: clamp(rawTop, margin, viewport.height - margin - height),
      side,
      maxHeight: room,
      maxWidth,
    };
  }

  const anchorRight = anchor.left + anchor.width;
  const right = viewport.width - anchorRight - gap - margin;
  const left = anchor.left - gap - margin;
  const side: FloatSide = panel.width <= right || right >= left ? "right" : "left";
  const room = Math.max(0, side === "right" ? right : left);
  const sideWidth = Math.min(width, room);
  const rawLeft = side === "right" ? anchorRight + gap : anchor.left - gap - sideWidth;
  const maxHeight = Math.max(0, viewport.height - margin * 2);
  const height = Math.min(panel.height, maxHeight);
  const rawTop = align === "end" ? anchor.top + anchor.height - height : anchor.top;
  return {
    left: clamp(rawLeft, margin, viewport.width - margin - sideWidth),
    top: clamp(rawTop, margin, viewport.height - margin - height),
    side,
    maxHeight,
    maxWidth: room,
  };
}
